import { useEmail } from '@/state/email';
import { useLife } from '@/state/life';
import { useDialogue } from '@/state/dialogue';
import { useNotifications } from '@/state/notifications';
import { formatRelativeTime } from '@/lib/time';
import { cn } from '@/lib/cn';
import { getEmailReplyTree } from '@/content/dialogue/email';
import type { DialogueChoice } from '@/content/dialogue/types';
import { sounds } from '@/audio/synth';

export function EmailView({ emailId }: { emailId: string }) {
  const email = useEmail((s) => s.emails.find((e) => e.id === emailId));
  const close = useEmail((s) => s.close);
  const toggleStar = useEmail((s) => s.toggleStar);
  const remove = useEmail((s) => s.remove);
  const replyChoice = useEmail((s) => s.replyChoice);
  const lawyerActive = useLife((s) => s.lawyerRetainerActiveSince !== null);
  const stopLawyer = useLife((s) => s.stopLawyerRetainer);
  const usedOneShots = useDialogue((s) => s.usedOneShots);
  const pendingReplyByThread = useDialogue((s) => s.pendingReplyByThread);

  if (!email) return null;
  const isLawyer = email.fromAddress === 'l.markowitz@markowitzandsons.law';
  const tree = getEmailReplyTree(email.fromNpc);
  const oneShotKey = `email:${email.fromNpc}`;
  const usedIds = usedOneShots[oneShotKey] ?? [];
  const visibleChoices = (tree?.rootChoices ?? []).filter((c) => !usedIds.includes(c.id));
  const isPending = pendingReplyByThread[oneShotKey] === true;

  const handleChoice = (c: DialogueChoice) => {
    replyChoice(email.id, c);
    sounds.notifyInfo();
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2 py-2">
        <button
          onClick={close}
          className="rounded p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]"
          aria-label="Back"
        >
          ←
        </button>
        <button
          onClick={() => toggleStar(email.id)}
          className={cn(
            'rounded p-1.5 hover:bg-[var(--bg-elevated)]',
            email.starred ? 'text-[var(--accent-primary)]' : 'text-[var(--text-muted)]',
          )}
          aria-label="Star"
        >
          ★
        </button>
        <button
          onClick={() => remove(email.id)}
          className="ml-auto rounded p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--negative)]"
          aria-label="Delete"
        >
          ✕
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-4">
        <h1 className="text-mono-md font-bold text-[var(--text-primary)]">{email.subject}</h1>
        <div className="mt-1 flex items-baseline justify-between gap-2 border-b border-[var(--border-subtle)] pb-2 text-mono-xs text-[var(--text-muted)]">
          <span>{email.fromAddress}</span>
          <span>{formatRelativeTime(email.timestamp)}</span>
        </div>
        <div className="mt-3 whitespace-pre-wrap text-mono-sm leading-relaxed text-[var(--text-primary)]">
          {email.body}
        </div>
        {isLawyer && (
          <div className="mt-4 rounded border border-[var(--critical)] bg-[var(--bg-surface)] p-3">
            {lawyerActive ? (
              <>
                <p className="text-mono-xs text-[var(--negative)]">
                  ⚠ Retainer active. $1,400/h draining your cash right now.
                </p>
                <button
                  onClick={() => {
                    stopLawyer();
                    useNotifications.getState().push({
                      surfaceId: 'email',
                      severity: 'info',
                      text: 'lawyer fired. he sent one final invoice.',
                    });
                  }}
                  className="mt-2 rounded border border-[var(--critical)] px-3 py-1 text-mono-xs text-[var(--critical)] hover:bg-[var(--critical)] hover:text-black"
                >
                  fire lawyer
                </button>
              </>
            ) : (
              <p className="text-mono-xs text-[var(--text-muted)]">retainer not active right now</p>
            )}
          </div>
        )}

        {/* Quick-reply choices. Each (sender, choice) pair has a hand-authored
            multi-paragraph NPC reply — no free text, no chat-shape expectation. */}
        <div className="mt-4 border-t border-[var(--border-subtle)] pt-3">
          {!tree && (
            <p className="text-mono-xs italic text-[var(--text-muted)]">
              {email.fromNpc === 'self' ? 'sent.' : 'no reply choices for this sender — auto-replies only.'}
            </p>
          )}
          {tree && visibleChoices.length === 0 && (
            <p className="text-mono-xs italic text-[var(--text-muted)]">nothing left to say to {email.fromNpc}.</p>
          )}
          {tree && visibleChoices.length > 0 && (
            <>
              <div className="mb-2 flex items-center gap-3 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                <span>reply</span>
                {isPending && (
                  <span className="text-[var(--accent-primary)] normal-case tracking-normal">
                    {email.fromNpc} is composing a reply…
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {visibleChoices.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleChoice(c)}
                    disabled={isPending}
                    className={cn(
                      'rounded border px-3 py-1 text-mono-xs transition-colors text-left',
                      isPending
                        ? 'border-[var(--border-subtle)] bg-[var(--bg-deep)] text-[var(--text-muted)] opacity-40 cursor-not-allowed'
                        : c.destructive
                        ? 'border-[var(--negative)] bg-[var(--bg-deep)] text-[var(--negative)] hover:bg-[var(--negative)] hover:text-black'
                        : 'border-[var(--border-strong)] bg-[var(--bg-deep)] text-[var(--text-primary)] hover:bg-[var(--accent-primary)] hover:text-black',
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
